import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Checkbox,
  Switch,
  Row,
  Col,
  Card,
  Typography,
  Tooltip,
  Flex,
  Button,
  message,
  Tag,
  Space,
  Divider,
} from "antd";
import {
  EditOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TagsOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import {
  CreatePromotionRequest,
  CustomerEligibility,
  DiscountType,
  ProductAppliesTo,
  PromoStatus,
  PromoType,
  PurchaseType,
  UpdatePromotionRequest,
} from "../../../../api/admin/webshop/Promotion";
import {
  useCreatePromoMutation,
  useGetPromoByIdQuery,
  useUpdatePromoConfigMutation,
} from "../../../../api/admin/webshop/webshopAdminApi";
import { useGetWsProductsQuery } from "../../../../api/webshop/wsProductsApi";
import {
  isUtcDateInPast,
  SGT_TIMEZONE,
  sgtDayjsToUtcIso,
  utcIsoToSgtDayjs,
} from "../promoDateUtils";
import ApplicableProductsModal from "./ApplicableProductsModal";
import dayjs, { Dayjs } from "dayjs";

const { Text, Title } = Typography;

function normalizeAppliesTo(value?: ProductAppliesTo): ProductAppliesTo {
  if (!value) return "ALL";
  if (value === "COLLECTIONS") return "TAGS";
  return value;
}

export interface CreatePromoModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  promoCode?: string;
  onSuccess?: () => void;
}

interface FormValues {
  promoCode: string;
  name: string;
  customerTitle: string;
  promoType: PromoType;
  status: PromoStatus;
  startAt: Dayjs | null;
  endAt: Dayjs | null;
  noExpiry: boolean;

  discountType: DiscountType;
  discountValue: number;
  maxCap?: number | null;
  currency: string;

  minOrderValue: number;
  customerEligibility: CustomerEligibility;
  purchaseType: PurchaseType;

  productAppliesTo: ProductAppliesTo;
  productIds: string[];
  excludedProductIds: string[];

  isUnlimitedGlobal: boolean;
  globalLimit?: number | null;
  perUserLimit: number;
  oneRedemptionPerOrder: boolean;

  bundleDeals: boolean;
  simplyPoints: boolean;
  membershipBenefits: boolean;
  otherPromoCode: boolean;
  validOnAffiliateOrders: boolean;

  notesDescription?: string;
  notesCampaign?: string;
  notesDistributionSource?: string;
}

const DEFAULT_INITIAL_VALUES: FormValues = {
  promoCode: "",
  name: "",
  customerTitle: "",
  promoType: "PROMO",
  status: "DRAFT",
  startAt: null,
  endAt: null,
  noExpiry: false,
  discountType: "PERCENTAGE",
  discountValue: 10,
  maxCap: 10,
  currency: "SGD",
  minOrderValue: 0,
  customerEligibility: "EVERYONE",
  purchaseType: "ALL",
  productAppliesTo: "ALL",
  productIds: [],
  excludedProductIds: [],
  isUnlimitedGlobal: false,
  globalLimit: 1000,
  perUserLimit: 1,
  oneRedemptionPerOrder: true,
  bundleDeals: false,
  simplyPoints: false,
  membershipBenefits: false,
  otherPromoCode: false,
  validOnAffiliateOrders: true,
  notesDescription: "",
  notesCampaign: "",
  notesDistributionSource: "",
};

export default function CreatePromoModalV2(props: CreatePromoModalV2Props) {
  const { isOpen, onClose, promoCode, onSuccess } = props;
  const [form] = Form.useForm<FormValues>();
  const isUpdate = Boolean(promoCode);
  const [isEdit, setIsEdit] = useState(false);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [productSelection, setProductSelection] = useState<{
    appliesTo: ProductAppliesTo;
    ids: string[];
    excludedIds: string[];
  }>({
    appliesTo: "ALL",
    ids: [],
    excludedIds: [],
  });

  // RTK Query API calls
  const { data: promoData, isLoading: isLoadingPromo } = useGetPromoByIdQuery(
    { promoCode: promoCode! },
    { skip: !promoCode || !isOpen },
  );

  const { data: productsData } = useGetWsProductsQuery(undefined, {
    skip: !isOpen,
  });

  const [postPromotion, { isLoading: isCreating }] = useCreatePromoMutation();
  const [putPromotion, { isLoading: isUpdating }] =
    useUpdatePromoConfigMutation();

  // Watch fields for reactive conditional validation & UI updates
  const watchedDiscountType = Form.useWatch("discountType", form);
  const watchedCurrency = Form.useWatch("currency", form);
  const watchedNoExpiry = Form.useWatch("noExpiry", form);
  const watchedStartAt = Form.useWatch("startAt", form);
  const watchedIsUnlimitedGlobal = Form.useWatch("isUnlimitedGlobal", form);
  const watchedValidOnAffiliateOrders = Form.useWatch(
    "validOnAffiliateOrders",
    form,
  );
  const watchedPromoType = Form.useWatch("promoType", form);

  const availableRegions = useMemo(() => {
    if (!productsData) return [];
    const tagsSet = new Set<string>();
    productsData.forEach((p) => {
      if (p.regions && Array.isArray(p.regions)) {
        p.regions.forEach((r) => r && tagsSet.add(r.trim()));
      }
    });
    return Array.from(tagsSet);
  }, [productsData]);

  const availableTags = useMemo(() => {
    if (!productsData) return [];
    const tagsSet = new Set<string>();
    productsData.forEach((p) => {
      if (p.productTags && typeof p.productTags === "string") {
        p.productTags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => tagsSet.add(t));
      }
    });
    return Array.from(tagsSet);
  }, [productsData]);

  // Sync / prefill form state
  useEffect(() => {
    if (!isOpen) {
      setIsEdit(false);
      setIsProductsModalOpen(false);
      return;
    }

    if (promoCode && promoData) {
      setIsEdit(false);
      const discountType: DiscountType =
        promoData.discount?.type?.toUpperCase() === "FIXED_AMOUNT"
          ? "FIXED_AMOUNT"
          : "PERCENTAGE";

      const hasNoExpiry = promoData.noExpiry === true || !promoData.endAt;
      const isUnlimited = promoData.globalLimit == null;
      const currentPromoType = (promoData.type as PromoType) || "PROMO";

      const appliesTo = normalizeAppliesTo(
        promoData.eligibility?.products?.appliesTo ||
          (promoData.eligibility?.allowedProducts &&
          promoData.eligibility.allowedProducts.length > 0
            ? "PRODUCTS"
            : "ALL"),
      );

      const productIds =
        promoData.eligibility?.products?.ids ||
        promoData.eligibility?.allowedProducts ||
        [];

      const excludedIds =
        promoData.eligibility?.products?.excludedIds ||
        promoData.eligibility?.excludedProducts ||
        [];

      form.setFieldsValue({
        promoCode: promoData.promoCode,
        name: promoData.name || promoData.promoCode,
        customerTitle:
          promoData.customerTitle || promoData.name || promoData.promoCode,
        promoType: currentPromoType,
        status: promoData.status || "DRAFT",
        startAt: utcIsoToSgtDayjs(promoData.startAt),
        endAt: hasNoExpiry ? null : utcIsoToSgtDayjs(promoData.endAt),
        noExpiry: hasNoExpiry,
        discountType,
        discountValue: promoData.discount?.value ?? 0,
        maxCap: promoData.discount?.maxCap ?? null,
        currency: promoData.discount?.currency || "SGD",
        minOrderValue: promoData.eligibility?.minOrderValue ?? 0,
        customerEligibility:
          promoData.eligibility?.customerEligibility || "EVERYONE",
        purchaseType: promoData.eligibility?.purchaseType || "ALL",
        productAppliesTo: appliesTo,
        productIds,
        excludedProductIds: excludedIds,
        isUnlimitedGlobal: isUnlimited,
        globalLimit: isUnlimited ? null : promoData.globalLimit,
        perUserLimit:
          promoData.perUserLimit ?? promoData.eligibility?.perUserLimit ?? 1,
        oneRedemptionPerOrder: promoData.oneRedemptionPerOrder ?? true,
        bundleDeals: promoData.combinations?.bundleDeals ?? false,
        simplyPoints: promoData.combinations?.simplyPoints ?? false,
        membershipBenefits: promoData.combinations?.membershipBenefits ?? false,
        otherPromoCode: promoData.combinations?.otherPromoCode ?? false,
        validOnAffiliateOrders:
          promoData.affiliate?.validOnAffiliateOrders ?? true,
        notesDescription:
          promoData.internalNotes?.description || promoData.description || "",
        notesCampaign: promoData.internalNotes?.campaign || "",
        notesDistributionSource:
          promoData.internalNotes?.distributionSource || "",
      });
      setProductSelection({
        appliesTo,
        ids: productIds,
        excludedIds,
      });
    } else if (!promoCode) {
      setIsEdit(true);
      form.resetFields();
      form.setFieldsValue({
        ...DEFAULT_INITIAL_VALUES,
        startAt: dayjs().tz(SGT_TIMEZONE).startOf("hour"),
        endAt: dayjs().tz(SGT_TIMEZONE).add(1, "month").startOf("hour"),
      });
      setProductSelection({
        appliesTo: "ALL",
        ids: [],
        excludedIds: [],
      });
    }
  }, [isOpen, promoCode, promoData, form]);

  const handleFinish = async (values: FormValues) => {
    // In update mode, clicking Edit enables the form fields
    if (isUpdate && !isEdit) {
      setIsEdit(true);
      return;
    }

    try {
      const startAtUtc = sgtDayjsToUtcIso(values.startAt);
      if (!startAtUtc) {
        message.error("Start date is required");
        return;
      }

      let endAtUtc: string | null = null;
      if (!values.noExpiry) {
        endAtUtc = sgtDayjsToUtcIso(values.endAt);
        if (!endAtUtc) {
          message.error("End date is required when 'No Expiry' is unchecked");
          return;
        }
        if (
          values.startAt &&
          values.endAt &&
          !values.endAt.isAfter(values.startAt)
        ) {
          message.error("End date must be strictly after Start date");
          return;
        }
      }

      if (values.status === "ACTIVE" && endAtUtc && isUtcDateInPast(endAtUtc)) {
        message.error(
          "Cannot set status to ACTIVE because the end date is already in the past",
        );
        return;
      }

      if (
        isUpdate &&
        !values.isUnlimitedGlobal &&
        typeof values.globalLimit === "number" &&
        promoData?.globalUsedCount != null &&
        values.globalLimit < promoData.globalUsedCount
      ) {
        message.error(
          `Total limit (${values.globalLimit}) cannot be lower than redeemed count (${promoData.globalUsedCount})`,
        );
        return;
      }

      const isPercentage = values.discountType === "PERCENTAGE";
      const discountPayload = {
        type: values.discountType,
        value: Number(values.discountValue),
        ...(isPercentage && values.maxCap != null && values.maxCap > 0
          ? { maxCap: Number(values.maxCap) }
          : {}),
        currency: values.currency || "SGD",
      };

      const appliesTo = productSelection.appliesTo || "ALL";
      const productIds = appliesTo === "ALL" ? [] : productSelection.ids || [];
      const excludedIds = productSelection.excludedIds || [];

      if (appliesTo !== "ALL" && productIds.length === 0) {
        message.error(
          `Please configure and select at least one item for '${appliesTo}'`,
        );
        return;
      }

      const eligibilityPayload = {
        minOrderValue: Number(values.minOrderValue || 0),
        customerEligibility: values.customerEligibility,
        purchaseType: values.purchaseType,
        products: {
          appliesTo,
          ids: productIds,
          excludedIds,
        },
      };

      const globalLimitValue = values.isUnlimitedGlobal
        ? null
        : values.globalLimit != null
          ? Number(values.globalLimit)
          : null;

      const combinationsPayload = {
        bundleDeals: Boolean(values.bundleDeals),
        simplyPoints: Boolean(values.simplyPoints),
        membershipBenefits: Boolean(values.membershipBenefits),
        otherPromoCode: Boolean(values.otherPromoCode),
      };

      const affiliatePayload = {
        validOnAffiliateOrders: Boolean(values.validOnAffiliateOrders),
      };

      const internalNotesPayload = {
        description: values.notesDescription?.trim() || undefined,
        campaign: values.notesCampaign?.trim() || undefined,
        distributionSource:
          values.notesDistributionSource?.trim() || undefined,
      };

      if (isUpdate) {
        const updatePayload: UpdatePromotionRequest = {
          promoCode: promoCode!,
          name: values.name.trim(),
          customerTitle: values.customerTitle.trim(),
          status: values.status,
          startAt: startAtUtc,
          endAt: values.noExpiry ? null : endAtUtc,
          noExpiry: values.noExpiry,
          discount: discountPayload,
          eligibility: eligibilityPayload,
          globalLimit: globalLimitValue,
          perUserLimit: Number(values.perUserLimit || 1),
          oneRedemptionPerOrder: Boolean(values.oneRedemptionPerOrder),
          combinations: combinationsPayload,
          affiliate: affiliatePayload,
          internalNotes: internalNotesPayload,
          updatedBy: "admin",
        };

        await putPromotion(updatePayload).unwrap();
        message.success("Promotion updated successfully");
      } else {
        const cleanPromoCode = values.promoCode.trim().toUpperCase();
        const createPayload: CreatePromotionRequest = {
          promoCode: cleanPromoCode,
          name: values.name.trim(),
          customerTitle: values.customerTitle.trim(),
          type: values.promoType || "PROMO",
          status: values.status || "DRAFT",
          startAt: startAtUtc,
          endAt: values.noExpiry ? null : endAtUtc,
          noExpiry: values.noExpiry,
          discount: discountPayload,
          eligibility: eligibilityPayload,
          globalLimit: globalLimitValue,
          perUserLimit: Number(values.perUserLimit || 1),
          oneRedemptionPerOrder: Boolean(values.oneRedemptionPerOrder),
          combinations: combinationsPayload,
          affiliate: affiliatePayload,
          internalNotes: internalNotesPayload,
          createdBy: "admin",
        };

        await postPromotion(createPayload).unwrap();
        message.success("Promotion created successfully");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) {
        message.error("Please fill in all required fields correctly");
      } else {
        console.error("Error saving promotion:", err);
        message.error(
          err?.data?.message || err?.message || "Failed to save promotion",
        );
      }
    }
  };

  const isFormDisabled = isUpdate && !isEdit;
  const isSaving = isCreating || isUpdating;

  // Handle updates from ApplicableProductsModal popup
  const persistProductSelection = (
    newAppliesTo: ProductAppliesTo,
    newSelectedIds: string[],
    newExcludedIds: string[],
  ) => {
    const appliesTo = normalizeAppliesTo(newAppliesTo);
    const ids = appliesTo === "ALL" ? [] : [...newSelectedIds];
    const excludedIds = [...newExcludedIds];
    setProductSelection({ appliesTo, ids, excludedIds });
    form.setFieldsValue({
      productAppliesTo: appliesTo,
      productIds: ids,
      excludedProductIds: excludedIds,
    });
  };

  // Helper labels for selected items
  const productLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    if (productsData) {
      productsData.forEach((p) => {
        map.set(
          p.productId,
          p.productTitle || p.title || p.productName || p.productId,
        );
      });
    }
    return map;
  }, [productsData]);

  return (
    <>
      <Modal
        title={isUpdate ? "Update Promotion" : "Create Promotion"}
        open={isOpen}
        onCancel={() => {
          setIsEdit(false);
          onClose();
        }}
        onOk={() => form.submit()}
        okText={isUpdate ? (isEdit ? "Update" : "Edit") : "Create"}
        confirmLoading={isSaving}
        width="80%"
        destroyOnClose
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          disabled={isFormDisabled}
          onFinish={handleFinish}
        >
          {/* TOP SECTION: 2 Balanced Columns */}
          <Row gutter={24}>
            {/* Left Card: General Information & Schedule */}
            <Col span={12}>
              <Card
                bordered
                style={{
                  height: "100%",
                  background: "#fafafa",
                  borderRadius: 8,
                }}
              >
                <Title level={4} style={{ marginTop: 0, marginBottom: 12 }}>
                  1. Promotion Details & Schedule
                </Title>

                {/* Code & Status */}
                <Row gutter={12}>
                  <Col span={14}>
                    <Form.Item
                      label="Discount Code"
                      name="promoCode"
                      rules={[
                        { required: true, message: "Enter a promo code" },
                        {
                          pattern: /^[A-Za-z0-9_-]+$/,
                          message: "Alphanumeric characters only",
                        },
                      ]}
                    >
                      <Input
                        placeholder="e.g. SUMMER2026"
                        disabled={isUpdate}
                        onChange={(e) => {
                          const val = e.target.value
                            .toUpperCase()
                            .replace(/\s+/g, "");
                          form.setFieldsValue({ promoCode: val });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      label="Status"
                      name="status"
                      rules={[{ required: true, message: "Select status" }]}
                    >
                      <Select
                        options={[
                          { label: "Draft", value: "DRAFT" },
                          { label: "Scheduled", value: "SCHEDULED" },
                          { label: "Active", value: "ACTIVE" },
                          { label: "Paused", value: "PAUSED" },
                          { label: "Expired", value: "EXPIRED" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Internal Name & Customer Title */}
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      label="Internal Promotion Name"
                      name="name"
                      rules={[
                        { required: true, message: "Enter internal name" },
                      ]}
                    >
                      <Input placeholder="e.g. July First Order Campaign" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Customer-Facing Title"
                      name="customerTitle"
                      rules={[
                        {
                          required: true,
                          message: "Enter customer-facing title",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. 10% off your first order" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Dates */}
                <Row gutter={12} align="middle">
                  <Col span={10}>
                    <Form.Item
                      label="Valid From (SGT)"
                      name="startAt"
                      rules={[{ required: true, message: "Select start date" }]}
                    >
                      <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="DD-MMM-YYYY HH:mm"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      label="Valid To (SGT)"
                      name="endAt"
                      rules={[
                        {
                          validator: async (_, val) => {
                            if (watchedNoExpiry) return Promise.resolve();
                            if (!val) {
                              return Promise.reject(
                                new Error("Select end date or check No Expiry"),
                              );
                            }
                            if (
                              watchedStartAt &&
                              !val.isAfter(watchedStartAt)
                            ) {
                              return Promise.reject(
                                new Error("End date must be after Start date"),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="DD-MMM-YYYY HH:mm"
                        disabled={watchedNoExpiry}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4} style={{ paddingTop: 8 }}>
                    <Form.Item name="noExpiry" valuePropName="checked" noStyle>
                      <Checkbox
                        onChange={(e) => {
                          if (e.target.checked) {
                            form.setFieldsValue({ endAt: null });
                          }
                        }}
                      >
                        No Expiry
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Visibility Switch */}
                <Form.Item
                  label={`Website Visibility: ${watchedPromoType === "PROMO" ? "Public" : "Private"}`}
                  name="promoType"
                  style={{ marginBottom: 12 }}
                >
                  <Switch
                    checked={watchedPromoType === "PROMO"}
                    checkedChildren="Public"
                    unCheckedChildren="Private"
                    disabled={isUpdate}
                    onChange={(checked) =>
                      form.setFieldsValue({
                        promoType: checked ? "PROMO" : "PROMO#PRIVATE",
                      })
                    }
                  />
                </Form.Item>

                {/* Notes & Marketing */}
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="Campaign Name" name="notesCampaign">
                      <Input placeholder="e.g. Welcome10 Launch" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Distribution Source"
                      name="notesDistributionSource"
                    >
                      <Select
                        allowClear
                        placeholder="Select source"
                        options={[
                          { label: "Email", value: "Email" },
                          { label: "Website Banner", value: "Website Banner" },
                          { label: "Affiliate", value: "Affiliate" },
                          { label: "Meta Ads", value: "Meta Ads" },
                          { label: "Google Ads", value: "Google Ads" },
                          {
                            label: "Customer Support",
                            value: "Customer Support",
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Internal Description"
                  name="notesDescription"
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="Internal campaign notes..."
                  />
                </Form.Item>
              </Card>
            </Col>

            {/* Right Card: Discount Configuration & Limits */}
            <Col span={12}>
              <Card
                bordered
                style={{
                  height: "100%",
                  background: "#fafafa",
                  borderRadius: 8,
                }}
              >
                <Title level={4} style={{ marginTop: 0, marginBottom: 12 }}>
                  2. Discount & Limits
                </Title>

                {/* Discount Type, Value, Max Cap & Currency */}
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item
                      label="Discount Type"
                      name="discountType"
                      rules={[{ required: true, message: "Select type" }]}
                    >
                      <Select
                        options={[
                          { label: "Percentage (%)", value: "PERCENTAGE" },
                          { label: "Fixed Amount ($)", value: "FIXED_AMOUNT" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Discount Value"
                      name="discountValue"
                      rules={[
                        { required: true, message: "Enter discount value" },
                        {
                          validator: async (_, val) => {
                            if (val == null || val <= 0) {
                              return Promise.reject(
                                new Error("Value must be > 0"),
                              );
                            }
                            if (
                              watchedDiscountType === "PERCENTAGE" &&
                              val > 100
                            ) {
                              return Promise.reject(
                                new Error("Percentage cannot exceed 100%"),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        min={0.01}
                        style={{ width: "100%" }}
                        addonAfter={
                          watchedDiscountType === "PERCENTAGE"
                            ? "%"
                            : watchedCurrency || "SGD"
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Max Cap"
                      name="maxCap"
                      rules={[
                        {
                          validator: async (_, val) => {
                            if (watchedDiscountType === "PERCENTAGE") {
                              if (val == null || val <= 0) {
                                return Promise.reject(
                                  new Error("Max cap required for %"),
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        min={0.01}
                        disabled={watchedDiscountType === "FIXED_AMOUNT"}
                        placeholder={
                          watchedDiscountType === "FIXED_AMOUNT"
                            ? "N/A"
                            : "e.g. 10.00"
                        }
                        addonAfter={watchedCurrency || "SGD"}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Currency, Min Subtotal, Eligibility & Purchase Type */}
                <Row gutter={12}>
                  <Col span={6}>
                    <Form.Item
                      label="Currency"
                      name="currency"
                      rules={[{ required: true, message: "Select currency" }]}
                    >
                      <Select
                        options={[
                          { label: "SGD", value: "SGD" },
                          { label: "USD", value: "USD" },
                          { label: "EUR", value: "EUR" },
                          { label: "GBP", value: "GBP" },
                          { label: "AUD", value: "AUD" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Min Subtotal"
                      name="minOrderValue"
                      rules={[{ required: true, message: "Enter min subtotal" }]}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Customer"
                      name="customerEligibility"
                      rules={[{ required: true, message: "Select eligibility" }]}
                    >
                      <Select
                        options={[
                          { label: "Everyone", value: "EVERYONE" },
                          { label: "First Order", value: "FIRST_ORDER" },
                          { label: "Returning", value: "RETURNING" },
                          { label: "Mikey's Club", value: "MIKEYS_CLUB" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Purchase Type"
                      name="purchaseType"
                      rules={[{ required: true, message: "Select purchase type" }]}
                    >
                      <Select
                        options={[
                          { label: "All", value: "ALL" },
                          { label: "New eSIM", value: "NEW_ESIM" },
                          { label: "Recharge", value: "RECHARGE" },
                          {
                            label: "Instant Recharge",
                            value: "INSTANT_RECHARGE",
                          },
                          { label: "Physical SIM", value: "PHYSICAL_SIM" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Redemption Limits */}
                <Row gutter={12} align="middle">
                  <Col span={8}>
                    <Tooltip title="Total number of redemptions across all users">
                      <Form.Item
                        label="Total Promo Limit"
                        name="globalLimit"
                        rules={[
                          {
                            validator: async (_, val) => {
                              if (watchedIsUnlimitedGlobal)
                                return Promise.resolve();
                              if (val == null || val < 1) {
                                return Promise.reject(
                                  new Error("Must be >= 1 or Unlimited"),
                                );
                              }
                              if (
                                isUpdate &&
                                promoData?.globalUsedCount != null &&
                                val < promoData.globalUsedCount
                              ) {
                                return Promise.reject(
                                  new Error(
                                    `Cannot be < used count (${promoData.globalUsedCount})`,
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          min={1}
                          disabled={watchedIsUnlimitedGlobal}
                          placeholder={
                            watchedIsUnlimitedGlobal ? "Unlimited" : "e.g. 1000"
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Tooltip>
                  </Col>
                  <Col span={4} style={{ paddingTop: 8 }}>
                    <Form.Item
                      name="isUnlimitedGlobal"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox
                        onChange={(e) => {
                          if (e.target.checked) {
                            form.setFieldsValue({ globalLimit: null });
                          }
                        }}
                      >
                        Unlimited
                      </Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Per User Limit"
                      name="perUserLimit"
                      rules={[
                        { required: true, message: "Enter per-user limit" },
                      ]}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={6} style={{ paddingTop: 8 }}>
                    <Form.Item
                      name="oneRedemptionPerOrder"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>1 per order</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Stacking & Affiliate */}
                <Flex vertical gap={6} style={{ marginTop: 8 }}>
                  <Text strong style={{ fontSize: 13 }}>
                    Combinations (Stacking):
                  </Text>
                  <Flex gap={16} wrap="wrap">
                    <Form.Item
                      name="bundleDeals"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Bundle Deals</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="simplyPoints"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>SIMPLY Points</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="membershipBenefits"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Membership Benefits</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="otherPromoCode"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Other Promo Codes</Checkbox>
                    </Form.Item>
                  </Flex>

                  <div style={{ marginTop: 6 }}>
                    <Form.Item
                      name="validOnAffiliateOrders"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Valid on affiliate-attributed orders</Checkbox>
                    </Form.Item>
                    {watchedValidOnAffiliateOrders && (
                      <Text
                        type="secondary"
                        style={{ display: "block", fontSize: 11, marginTop: 4 }}
                      >
                        * Note: Discount will stack alongside affiliate
                        commissions.
                      </Text>
                    )}
                  </div>
                </Flex>
              </Card>
            </Col>
          </Row>

          {/* BOTTOM SECTION: Applicable Products & Exclusions */}
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                bordered
                style={{
                  background: "#fafafa",
                  borderRadius: 8,
                }}
              >
                <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      3. Applicable Products & Exclusions
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Define whether this promotion applies to all products,
                      selected products, variants, regions, or tags.
                    </Text>
                  </div>
                  <Button
                    icon={<EditOutlined />}
                    disabled={isFormDisabled}
                    onClick={() => setIsProductsModalOpen(true)}
                    style={{
                      borderColor: "rgb(255, 70, 37)",
                      color: "rgb(255, 70, 37)",
                    }}
                  >
                    Configure Products / Variants
                  </Button>
                </Flex>

                <Divider style={{ margin: "12px 0" }} />

                <Row gutter={16} align="middle">
                  <Col span={6}>
                    <Form.Item
                      label="Applies To Scope"
                      name="productAppliesTo"
                      rules={[{ required: true, message: "Select scope" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        onChange={(val) => {
                          persistProductSelection(
                            val,
                            val === "ALL" ? [] : productSelection.ids,
                            productSelection.excludedIds,
                          );
                          if (val !== "ALL") {
                            setIsProductsModalOpen(true);
                          }
                        }}
                        options={[
                          { label: "All Products in Catalog", value: "ALL" },
                          { label: "Selected Products", value: "PRODUCTS" },
                          { label: "Selected Variants", value: "VARIANTS" },
                          { label: "Regions", value: "REGIONS" },
                          { label: "Tags", value: "TAGS" },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item name="productIds" hidden>
                      <Select mode="multiple" />
                    </Form.Item>
                    <Form.Item name="excludedProductIds" hidden>
                      <Select mode="multiple" />
                    </Form.Item>
                  </Col>

                  <Col span={18}>
                    <Text strong style={{ display: "block", marginBottom: 6 }}>
                      Current Selection Summary:
                    </Text>
                    <Flex gap={8} wrap="wrap" align="center">
                      {productSelection.appliesTo === "ALL" ? (
                        <Tag color="blue" icon={<GlobalOutlined />}>
                          Applies to All Products in Catalog
                        </Tag>
                      ) : productSelection.appliesTo === "PRODUCTS" ? (
                        productSelection.ids.length > 0 ? (
                          <>
                            <Tag color="orange" icon={<ShoppingOutlined />}>
                              {productSelection.ids.length} Products Selected
                            </Tag>
                            {productSelection.ids.slice(0, 5).map((id) => (
                              <Tag key={id}>
                                {productLabelMap.get(id) || id}
                              </Tag>
                            ))}
                            {productSelection.ids.length > 5 && (
                              <Tag color="default">
                                +{productSelection.ids.length - 5} more
                              </Tag>
                            )}
                          </>
                        ) : (
                          <Text type="danger">
                            No products selected yet. Click Configure to select.
                          </Text>
                        )
                      ) : productSelection.appliesTo === "VARIANTS" ? (
                        productSelection.ids.length > 0 ? (
                          <>
                            <Tag color="orange" icon={<AppstoreOutlined />}>
                              {productSelection.ids.length} Variants Selected
                            </Tag>
                            {productSelection.ids.slice(0, 5).map((id) => (
                              <Tag key={id}>{id}</Tag>
                            ))}
                            {productSelection.ids.length > 5 && (
                              <Tag color="default">
                                +{productSelection.ids.length - 5} more
                              </Tag>
                            )}
                          </>
                        ) : (
                          <Text type="danger">
                            No variants selected yet. Click Configure to select.
                          </Text>
                        )
                      ) : productSelection.appliesTo === "REGIONS" ? (
                        productSelection.ids.length > 0 ? (
                          <>
                            <Tag color="orange" icon={<GlobalOutlined />}>
                              {productSelection.ids.length} Regions Selected
                            </Tag>
                            {productSelection.ids.map((item) => (
                              <Tag key={item}>{item}</Tag>
                            ))}
                          </>
                        ) : (
                          <Text type="danger">
                            No regions selected yet. Click Configure to select.
                          </Text>
                        )
                      ) : productSelection.ids.length > 0 ? (
                        <>
                          <Tag color="orange" icon={<TagsOutlined />}>
                            {productSelection.ids.length} Tags Selected
                          </Tag>
                          {productSelection.ids.map((item) => (
                            <Tag key={item} color="geekblue">
                              {item}
                            </Tag>
                          ))}
                        </>
                      ) : (
                        <Text type="danger">
                          No tags selected yet. Click Configure to select.
                        </Text>
                      )}

                      {productSelection.excludedIds.length > 0 && (
                        <>
                          <Divider type="vertical" />
                          <Tag color="red">
                            {productSelection.excludedIds.length} Excluded
                          </Tag>
                          {productSelection.excludedIds.slice(0, 3).map((id) => (
                            <Tag key={id} color="error">
                              {productLabelMap.get(id) || id}
                            </Tag>
                          ))}
                          {productSelection.excludedIds.length > 3 && (
                            <Tag color="error">
                              +{productSelection.excludedIds.length - 3} more
                            </Tag>
                          )}
                        </>
                      )}
                    </Flex>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 2-Section Popup Modal to Configure Applicable Products / Variants / Collections */}
      <ApplicableProductsModal
        isOpen={isProductsModalOpen}
        onClose={() => setIsProductsModalOpen(false)}
        onApply={persistProductSelection}
        initialAppliesTo={productSelection.appliesTo}
        initialSelectedIds={productSelection.ids}
        initialExcludedIds={productSelection.excludedIds}
        products={productsData || []}
        regionOptions={availableRegions}
        tagOptions={availableTags}
      />
    </>
  );
}
